import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Select,
  Table,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Tbody,
  useDisclosure,
  useToast,
  VStack,
  Code,
  Spinner,
  Center,
  Divider,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { LeagueId, Match, Prediction } from '../../types';
import { ResponsiveTableContainer } from '../ui/ResponsiveTableContainer';
import { KNOCKOUT_ROUNDS } from '../../constants/mundialBracket';
import {
  filterMatchesByMundialJornada,
  getJornadaLabel,
  getMundialGroupMatches,
  getMundialKnockoutMatches,
  parseLigaMxJornadaNumber,
} from '../../services/matchdayService';

const LEAGUE_NAMES: Record<LeagueId, string> = {
  liga_mx: 'Liga BBVA MX',
  mundial_fifa: 'Mundial FIFA 2026',
};

const MUNDIAL_KNOCKOUT_FILTER = [
  { value: '', label: 'Todas las eliminatorias' },
  ...KNOCKOUT_ROUNDS.map((r) => ({ value: r.matchType, label: r.phaseLabel })),
];

function formatMatchDate(m: Match): string {
  const d = m.date instanceof Date ? m.date : new Date(m.date);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-MX', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function sortMatches(a: Match, b: Match): number {
  const ta = new Date(a.date).getTime();
  const tb = new Date(b.date).getTime();
  if (ta !== tb) return ta - tb;
  return (a.bracket_slot ?? a.group ?? a.phase ?? '').localeCompare(
    b.bracket_slot ?? b.group ?? b.phase ?? ''
  );
}

async function fetchPredictionCounts(matchIds: string[]): Promise<Map<string, number>> {
  const counts = new Map<string, number>();
  if (matchIds.length === 0) return counts;

  const predictionsRef = collection(db, 'predictions');
  for (let i = 0; i < matchIds.length; i += 10) {
    const chunk = matchIds.slice(i, i + 10);
    const snap = await getDocs(query(predictionsRef, where('match_id', 'in', chunk)));
    snap.docs.forEach((d) => {
      const matchId = d.data().match_id as string;
      counts.set(matchId, (counts.get(matchId) ?? 0) + 1);
    });
  }
  return counts;
}

async function deleteAllPredictionsForMatch(matchId: string): Promise<number> {
  const snap = await getDocs(
    query(collection(db, 'predictions'), where('match_id', '==', matchId))
  );
  if (snap.empty) return 0;

  let deleted = 0;
  for (let i = 0; i < snap.docs.length; i += 500) {
    const batch = writeBatch(db);
    snap.docs.slice(i, i + 500).forEach((d) => batch.delete(d.ref));
    await batch.commit();
    deleted += Math.min(500, snap.docs.length - i);
  }
  return deleted;
}

async function fetchPredictionsForMatch(matchId: string): Promise<Prediction[]> {
  const snap = await getDocs(
    query(collection(db, 'predictions'), where('match_id', '==', matchId))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as Prediction));
}

export const ResetMatchPredictions: React.FC = () => {
  const [league, setLeague] = useState<LeagueId>('mundial_fifa');
  const [matches, setMatches] = useState<Match[]>([]);
  const [predictionCounts, setPredictionCounts] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [ligaPhase, setLigaPhase] = useState('');
  const [mundialJornada, setMundialJornada] = useState('');
  const [mundialKnockout, setMundialKnockout] = useState('round_of_32');
  const [mundialScope, setMundialScope] = useState<'groups' | 'knockout'>('knockout');
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [matchPredictions, setMatchPredictions] = useState<Prediction[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [matchToDelete, setMatchToDelete] = useState<Match | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const toast = useToast();

  const ligaPhases = useMemo(() => {
    if (league !== 'liga_mx') return [];
    return Array.from(
      new Set(
        matches
          .map((m) => m.phase)
          .filter((p) => p && parseLigaMxJornadaNumber(p) !== null)
      )
    ).sort(
      (a, b) =>
        (parseLigaMxJornadaNumber(a) ?? 0) - (parseLigaMxJornadaNumber(b) ?? 0)
    );
  }, [league, matches]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'matches'));
      const all = snap.docs
        .map((d) => {
          const data = d.data();
          return {
            id: d.id,
            ...data,
            date: data.date?.toDate?.() ?? data.date,
          } as Match;
        })
        .filter((m) => (m.league ?? 'liga_mx') === league)
        .sort(sortMatches);

      setMatches(all);

      const ids = all.map((m) => m.id);
      const counts = await fetchPredictionCounts(ids);
      setPredictionCounts(counts);
    } catch (e) {
      toast({
        title: 'Error al cargar',
        description: e instanceof Error ? e.message : 'Error desconocido',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [league, toast]);

  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  useEffect(() => {
    if (league === 'liga_mx' && ligaPhases.length > 0 && !ligaPhase) {
      setLigaPhase(ligaPhases[0]);
    }
  }, [league, ligaPhases, ligaPhase]);

  const filtered = useMemo(() => {
    let list = matches;

    if (league === 'liga_mx') {
      if (ligaPhase) list = list.filter((m) => m.phase === ligaPhase);
    } else if (mundialScope === 'groups') {
      const groupPool = getMundialGroupMatches(matches);
      if (mundialJornada) {
        list = filterMatchesByMundialJornada(
          groupPool,
          Number(mundialJornada),
          groupPool
        );
      } else {
        list = groupPool;
      }
    } else {
      list = getMundialKnockoutMatches(matches);
      if (mundialKnockout) {
        list = list.filter((m) => m.match_type === mundialKnockout);
      }
    }

    const q = search.trim().toLowerCase();
    if (!q) return list;

    return list.filter((m) => {
      const haystack = [
        m.id,
        m.bracket_slot,
        m.group,
        m.phase,
        m.team_a,
        m.team_b,
        m.team_a_code,
        m.team_b_code,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [
    matches,
    league,
    ligaPhase,
    mundialScope,
    mundialJornada,
    mundialKnockout,
    search,
  ]);

  const openMatchDetail = async (match: Match) => {
    setSelectedMatch(match);
    setLoadingDetail(true);
    try {
      const preds = await fetchPredictionsForMatch(match.id);
      setMatchPredictions(preds);
    } catch (e) {
      toast({
        title: 'No se pudieron cargar los pronósticos',
        description: e instanceof Error ? e.message : 'Error',
        status: 'error',
      });
      setMatchPredictions([]);
    } finally {
      setLoadingDetail(false);
    }
  };

  const confirmDeleteAll = (match: Match) => {
    setMatchToDelete(match);
    onOpen();
  };

  const executeDeleteAll = async () => {
    if (!matchToDelete) return;
    setDeleting(true);
    try {
      const count = await deleteAllPredictionsForMatch(matchToDelete.id);
      toast({
        title: 'Pronósticos eliminados',
        description: `${count} pronóstico(s) del partido ${matchToDelete.team_a} vs ${matchToDelete.team_b}`,
        status: 'success',
        duration: 5000,
      });
      if (selectedMatch?.id === matchToDelete.id) {
        setMatchPredictions([]);
      }
      await load();
      onClose();
      setMatchToDelete(null);
    } catch (e) {
      toast({
        title: 'Error al eliminar',
        description: e instanceof Error ? e.message : 'Error',
        status: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  const deleteOne = async (prediction: Prediction) => {
    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'predictions', prediction.id));
      setMatchPredictions((prev) => prev.filter((p) => p.id !== prediction.id));
      setPredictionCounts((prev) => {
        const next = new Map(prev);
        const current = next.get(prediction.match_id) ?? 0;
        if (current <= 1) next.delete(prediction.match_id);
        else next.set(prediction.match_id, current - 1);
        return next;
      });
      toast({ title: 'Pronóstico eliminado', status: 'success', duration: 3000 });
    } catch (e) {
      toast({
        title: 'Error',
        description: e instanceof Error ? e.message : 'Error',
        status: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  const totalInFilter = filtered.reduce(
    (sum, m) => sum + (predictionCounts.get(m.id) ?? 0),
    0
  );

  if (loading && matches.length === 0) {
    return (
      <Center py={10}>
        <Spinner />
      </Center>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      <Box>
        <Heading size="lg" mb={2}>
          Reiniciar pronósticos por partido
        </Heading>
        <Text fontSize="sm" color="gray.600">
          Elimina pronósticos capturados de un solo juego. No borra toda la jornada ni
          modifica resultados del partido.
        </Text>
      </Box>

      <Alert status="warning" fontSize="sm">
        Esta acción no se puede deshacer. Los usuarios tendrán que volver a capturar ese
        partido.
      </Alert>

      <HStack flexWrap="wrap" spacing={3} align="flex-end">
        <FormControl maxW="xs">
          <FormLabel fontSize="sm">Torneo</FormLabel>
          <Select
            size="sm"
            value={league}
            onChange={(e) => {
              setLeague(e.target.value as LeagueId);
              setSelectedMatch(null);
              setMatchPredictions([]);
              setLigaPhase('');
              setMundialJornada('');
            }}
          >
            {(Object.keys(LEAGUE_NAMES) as LeagueId[]).map((id) => (
              <option key={id} value={id}>
                {LEAGUE_NAMES[id]}
              </option>
            ))}
          </Select>
        </FormControl>

        {league === 'liga_mx' ? (
          <FormControl maxW="xs">
            <FormLabel fontSize="sm">Jornada</FormLabel>
            <Select
              size="sm"
              value={ligaPhase}
              onChange={(e) => setLigaPhase(e.target.value)}
            >
              {ligaPhases.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </FormControl>
        ) : (
          <>
            <FormControl maxW="xs">
              <FormLabel fontSize="sm">Fase</FormLabel>
              <Select
                size="sm"
                value={mundialScope}
                onChange={(e) =>
                  setMundialScope(e.target.value as 'groups' | 'knockout')
                }
              >
                <option value="groups">Grupos</option>
                <option value="knockout">Eliminatorias</option>
              </Select>
            </FormControl>
            {mundialScope === 'groups' ? (
              <FormControl maxW="xs">
                <FormLabel fontSize="sm">Jornada de grupos</FormLabel>
                <Select
                  size="sm"
                  value={mundialJornada}
                  onChange={(e) => setMundialJornada(e.target.value)}
                >
                  <option value="">Todas</option>
                  {[1, 2, 3].map((j) => (
                    <option key={j} value={String(j)}>
                      {getJornadaLabel('mundial_fifa', j)}
                    </option>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <FormControl maxW="xs">
                <FormLabel fontSize="sm">Ronda</FormLabel>
                <Select
                  size="sm"
                  value={mundialKnockout}
                  onChange={(e) => setMundialKnockout(e.target.value)}
                >
                  {MUNDIAL_KNOCKOUT_FILTER.map((o) => (
                    <option key={o.value || 'all'} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
          </>
        )}

        <FormControl flex="1" minW="200px">
          <FormLabel fontSize="sm">Buscar partido</FormLabel>
          <Input
            size="sm"
            placeholder="Equipo, slot, match_id…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </FormControl>

        <Button size="sm" variant="outline" onClick={() => load()}>
          Recargar
        </Button>
      </HStack>

      <Text fontSize="sm" color="gray.500">
        {filtered.length} partido(s) · {totalInFilter} pronóstico(s) en esta vista
      </Text>

      <ResponsiveTableContainer>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Fecha</Th>
              <Th>Partido</Th>
              <Th>match_id</Th>
              <Th isNumeric>Pronósticos</Th>
              <Th>Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filtered.map((m) => {
              const count = predictionCounts.get(m.id) ?? 0;
              return (
                <Tr
                  key={m.id}
                  bg={selectedMatch?.id === m.id ? 'blue.50' : undefined}
                  _hover={{ bg: selectedMatch?.id === m.id ? 'blue.50' : 'gray.50' }}
                >
                  <Td whiteSpace="nowrap">{formatMatchDate(m)}</Td>
                  <Td>
                    <Text fontWeight="medium">
                      {m.team_a_code ?? m.team_a} vs {m.team_b_code ?? m.team_b}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {m.bracket_slot ?? m.group ?? m.phase}
                    </Text>
                  </Td>
                  <Td>
                    <Code fontSize="xs">{m.id}</Code>
                  </Td>
                  <Td isNumeric>
                    <Badge colorScheme={count > 0 ? 'blue' : 'gray'}>{count}</Badge>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => openMatchDetail(m)}
                        isDisabled={count === 0}
                      >
                        Ver
                      </Button>
                      <Button
                        size="xs"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => confirmDeleteAll(m)}
                        isDisabled={count === 0 || deleting}
                      >
                        Eliminar todos
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </ResponsiveTableContainer>

      {filtered.length === 0 && (
        <Text textAlign="center" color="gray.500" fontSize="sm">
          No hay partidos con ese filtro.
        </Text>
      )}

      {selectedMatch && (
        <>
          <Divider />
          <Box p={4} borderWidth="1px" borderRadius="md">
            <Heading size="sm" mb={3}>
              Pronósticos — {selectedMatch.team_a} vs {selectedMatch.team_b}
            </Heading>
            <Text fontSize="xs" color="gray.500" mb={3}>
              match_id: <Code>{selectedMatch.id}</Code>
            </Text>

            {loadingDetail ? (
              <Spinner size="sm" />
            ) : matchPredictions.length === 0 ? (
              <Text fontSize="sm" color="gray.500">
                Sin pronósticos para este partido.
              </Text>
            ) : (
              <ResponsiveTableContainer>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Usuario</Th>
                      <Th>Marcador</Th>
                      <Th />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {matchPredictions.map((p) => (
                      <Tr key={p.id}>
                        <Td>
                          <Code fontSize="xs">{p.user_id}</Code>
                        </Td>
                        <Td>
                          {p.predicted_score_a} - {p.predicted_score_b}
                        </Td>
                        <Td>
                          <IconButton
                            aria-label="Eliminar pronóstico"
                            icon={<DeleteIcon />}
                            size="xs"
                            colorScheme="red"
                            variant="ghost"
                            isLoading={deleting}
                            onClick={() => deleteOne(p)}
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </ResponsiveTableContainer>
            )}

            {matchPredictions.length > 0 && (
              <Button
                mt={4}
                size="sm"
                colorScheme="red"
                variant="outline"
                onClick={() => confirmDeleteAll(selectedMatch)}
                isLoading={deleting}
              >
                Eliminar los {matchPredictions.length} pronósticos de este partido
              </Button>
            )}
          </Box>
        </>
      )}

      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Eliminar pronósticos del partido</AlertDialogHeader>
            <AlertDialogBody>
              {matchToDelete && (
                <>
                  Se eliminarán{' '}
                  <strong>{predictionCounts.get(matchToDelete.id) ?? 0}</strong> pronóstico(s)
                  de{' '}
                  <strong>
                    {matchToDelete.team_a} vs {matchToDelete.team_b}
                  </strong>
                  . Los demás partidos de la jornada no se tocan. El resultado capturado del
                  partido en Administración → Resultados no se modifica.
                </>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={executeDeleteAll} isLoading={deleting} ml={3}>
                Eliminar pronósticos
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  );
};

export default ResetMatchPredictions;
